import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { SessionStorageService } from '../../../services/session-storage.service';
import { AuthService } from '../../../services/auth.service';
import { Router } from '@angular/router';
import { AnalyticsService } from '../../../services/analytics.service';
import { ConfigService } from '../../../core/config/config.service';
import { SistemaService, ServerStatus } from '../../../services/sistema.service';
import { Usuario } from '../../../models/auth.model';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-user-modal',
  imports: [DatePipe],
  standalone: true,
  templateUrl: './user-modal.html',
  styleUrl: './user-modal.css',
})
export class UserModal implements OnInit, OnChanges, OnDestroy {

  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();

  usuarioLogado: Usuario | null = null;
  ultimoAcesso = new Date();
  serverStatus: ServerStatus | null = null;
  isLoadingStatus = true;
  hasStatusError = false;

  /** Estado do rastreamento GPS (permissão de geolocalização do navegador). */
  gpsStatus: 'verificando' | 'ativo' | 'inativo' = 'verificando';

  private userSubscription?: Subscription;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sessionStorageService = inject(SessionStorageService);

  constructor(
    private authService: AuthService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private configService: ConfigService,
    private sistemaService: SistemaService
  ) {}

  ngOnInit(): void {
    this.usuarioLogado = this.authService.getUser();
    this.userSubscription = this.authService.user$.subscribe((user) => {
      this.usuarioLogado = user;
      this.ultimoAcesso = new Date();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.loadServerStatus();
      this.checkGpsStatus();
    }
  }

  /** Verifica se o rastreamento GPS está ativo via permissão de geolocalização. */
  private checkGpsStatus(): void {
    this.gpsStatus = 'verificando';
    this.cdr.markForCheck();

    if (!('geolocation' in navigator)) {
      this.gpsStatus = 'inativo';
      this.cdr.markForCheck();
      return;
    }

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((result) => {
          this.gpsStatus = result.state === 'granted' ? 'ativo' : 'inativo';
          this.cdr.markForCheck();
          result.onchange = () => {
            this.gpsStatus = result.state === 'granted' ? 'ativo' : 'inativo';
            this.cdr.markForCheck();
          };
        })
        .catch(() => {
          this.gpsStatus = 'inativo';
          this.cdr.markForCheck();
        });
    } else {
      // Sem Permissions API: geolocalização existe mas não é possível confirmar o estado.
      this.gpsStatus = 'inativo';
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
  }

  private loadServerStatus(): void {
    this.isLoadingStatus = true;
    this.hasStatusError = false;
    this.serverStatus = null;
    this.cdr.markForCheck();

    this.sistemaService.getStatus().subscribe({
      next: (status) => {
        this.serverStatus = status;
        this.isLoadingStatus = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.hasStatusError = true;
        this.isLoadingStatus = false;
        this.cdr.markForCheck();
      }
    });
  }

  get clientVersion(): string {
    return this.configService.settings()?.version || 'Não informado';
  }

  get nomeExibicao(): string {
    return this.usuarioLogado?.nomeCompleto || 'Usuário não identificado';
  }

  get username(): string {
    return this.usuarioLogado?.nomeUsuario || 'Não informado';
  }

  /** Slug da empresa da sessão; nulo em modo single-tenant. */
  get tenant(): string | null {
    return this.sessionStorageService.getTenant();
  }

  get email(): string {
    return this.usuarioLogado?.email || 'Não informado';
  }

  get perfilUsuario(): string {
    return this.authService.permissoes()?.length ? 'Usuário com permissões ativas' : 'Usuário do Sistema';
  }

  public onClose(): void {
    this.closeModal.emit();
  }

  atualizarCadastro(): void {
    this.onClose();
    this.router.navigate(['/home', 'usuario-manutencao']);
  }

  logout(): void {
    this.analyticsService.saveUsage({ username: this.authService.getUser()?.nomeUsuario || 'undefined_user', acao: 'logout' }).subscribe();
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
